import React, { useState, useRef, useEffect } from "react";

import "./autocomplete.scss";

export default function Autocomplete({
  items = [],
  value = "",
  onChange,
  onSelect,
  placeholder = "Select...",
  required = false,
  disabled = false,
  getItemProps = () => ({}),
  allowClear = true,
  renderItem,
  emptyMsg,
  ...rest
}) {
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [inputValue]);

  useEffect(() => {
    if (!isOpen) {
      if (value === "" || value === undefined) {
        setInputValue("");
      } else {
        const selected = items.find(
          (opt) => opt.value?.toString() === value?.toString(),
        );
        setInputValue(selected ? selected.name : "");
      }
    }
  }, [value, items, isOpen]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions =
    isTyping && inputValue
      ? items.filter((opt) =>
          (opt.name ? opt.name.toLowerCase() : "").includes(
            inputValue.toLowerCase(),
          ),
        )
      : items;

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    setIsOpen(true);
    setHighlightedIndex(-1);
    setIsTyping(true);
    if (onChange) onChange(e.target.value);
  };

  const handleOptionClick = (option) => {
    if (option.disabled) return;
    setInputValue(option.name);
    setIsOpen(false);
    setHighlightedIndex(-1);
    if (onSelect) onSelect(option);
    if (onChange) onChange(option.value);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    setIsTyping(false);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) return;
    if (e.key === "ArrowDown") {
      setHighlightedIndex((prev) =>
        prev < filteredOptions.length - 1 ? prev + 1 : 0,
      );
    } else if (e.key === "ArrowUp") {
      setHighlightedIndex((prev) =>
        prev > 0 ? prev - 1 : filteredOptions.length - 1,
      );
    } else if (e.key === "Enter") {
      if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
        handleOptionClick(filteredOptions[highlightedIndex]);
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setHighlightedIndex(-1);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setInputValue(undefined);
    setIsOpen(false);
    setHighlightedIndex(-1);
    if (onSelect) onSelect({ value: undefined, name: "" });
    if (onChange) onChange(undefined);
  };

  const showClear =
    allowClear && value !== "" && value !== null && value !== undefined;

  return (
    <div
      ref={containerRef}
      className={"autocomplete " + (allowClear ? "allow-clear" : "")}
    >
      <input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        autoComplete="off"
        {...rest}
        className="autocomplete__input"
      />
      {isOpen && (
        <ul className="autocomplete__list" ref={listRef}>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, idx) => (
              <li
                key={idx}
                className={
                  `autocomplete__option` +
                  (idx === highlightedIndex
                    ? " autocomplete__option--highlighted"
                    : "") +
                  (option.disabled ? " autocomplete__option--disabled" : "") +
                  (option.className ? ` ${option.className}` : "")
                }
                onClick={() => handleOptionClick(option)}
                onMouseEnter={() => setHighlightedIndex(idx)}
                {...getItemProps(option)}
              >
                {renderItem ? renderItem(option) : option.name}
              </li>
            ))
          ) : (
            <li className="autocomplete__no-items">
              {emptyMsg || "No items found"}
            </li>
          )}
        </ul>
      )}
      {allowClear && (
        <button
          type="button"
          className="autocomplete__clear"
          onClick={handleClear}
          tabIndex={-1}
          aria-label="Clear"
          disabled={disabled || !showClear}
        >
          ×
        </button>
      )}
    </div>
  );
}
